import { useState, type ReactNode } from 'react';
import { Check, Copy, X } from 'lucide-react';

/** Occasions the CRM offers when building a pop-up, each with a theme that suits it. */
export const OCCASIONS: { id: string; label: string; theme: PopupTheme }[] = [
  { id: 'general', label: 'Announcement', theme: 'ink' },
  { id: 'sale', label: 'Sale or discount', theme: 'gold' },
  { id: 'launch', label: 'New launch', theme: 'ocean' },
  { id: 'eid', label: 'Eid', theme: 'emerald' },
  { id: 'ramadan', label: 'Ramadan', theme: 'emerald' },
  { id: 'christmas', label: 'Christmas', theme: 'festive' },
  { id: 'newyear', label: 'New Year', theme: 'festive' },
  { id: 'blackfriday', label: 'Black Friday', theme: 'ink' },
  { id: 'independence', label: 'Independence / national day', theme: 'emerald' },
  { id: 'anniversary', label: 'CK anniversary', theme: 'gold' },
  { id: 'maintenance', label: 'Service notice', theme: 'sand' },
];
export type PopupTheme = 'gold' | 'ink' | 'sand' | 'festive' | 'emerald' | 'ocean';
export const THEMES: { id: PopupTheme; label: string; swatch: [string, string] }[] = [
  { id: 'gold', label: 'Gold', swatch: ['#F2B705', '#0D0C0A'] },
  { id: 'ink', label: 'Night', swatch: ['#0D0C0A', '#F2B705'] },
  { id: 'sand', label: 'Sand', swatch: ['#F5F1EA', '#B07A45'] },
  { id: 'festive', label: 'Festive', swatch: ['#7A1020', '#F2C14E'] },
  { id: 'emerald', label: 'Emerald', swatch: ['#0E4D3A', '#E8C66A'] },
  { id: 'ocean', label: 'Ocean', swatch: ['#0F2D5C', '#7FD1FF'] },
];
export const occasionLabel = (id: string) => OCCASIONS.find((o) => o.id === id)?.label ?? id;

export interface PromoCardProps {
  theme: string; occasion?: string; kicker?: string; title: string; body?: string; image?: string | null;
  couponCode?: string | null; ctaLabel?: string; onCta?: () => void; onClose?: () => void; onDecline?: () => void; declineLabel?: string;
  footer?: ReactNode; children?: ReactNode; titleId?: string;
}

/** One pop-up, drawn the same way in the client portal and in the CRM preview. */
export function PromoCard({ theme, occasion, kicker, title, body, image, couponCode, ctaLabel, onCta, onClose, onDecline, declineLabel = 'No thanks', footer, children, titleId }: PromoCardProps) {
  const [copied, setCopied] = useState(false);
  const copy = async () => { if (!couponCode) return; try { await navigator.clipboard.writeText(couponCode); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* clipboard blocked */ } };
  return (
    <section className={`pp pp-${theme}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <span className="pp-motif" aria-hidden="true" />
      {onClose && <button type="button" className="pp-x" aria-label="Close" onClick={onClose}><X /></button>}
      {image && <img className="pp-img" src={image} alt="" />}
      <div className="pp-in">
        {(kicker || occasion) && <p className="pp-kick">{kicker ?? occasionLabel(occasion!)}</p>}
        <h2 className="pp-h" id={titleId}>{title}</h2>
        {body && <p className="pp-body">{body}</p>}
        {children}
        {couponCode && (
          <div className="pp-code">
            <span><small>Code</small><b>{couponCode}</b></span>
            <button type="button" onClick={() => void copy()}>{copied ? <><Check />Copied</> : <><Copy />Copy</>}</button>
          </div>
        )}
        {(ctaLabel || onDecline) && (
          <div className="pp-actions">
            {onDecline && <button type="button" className="pp-no" onClick={onDecline}>{declineLabel}</button>}
            {ctaLabel && <button type="button" className="pp-cta" onClick={onCta}>{ctaLabel}</button>}
          </div>
        )}
        {footer}
      </div>
    </section>
  );
}
